import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { State } from 'reducers';
import { ThemeId, BuildingId, DeviceId } from 'shared/models';
import { getBuildingDevices } from 'selectors/buildingsSelectors';
import { getTheme } from 'selectors/themesSelectors';
import { getSchedules } from 'selectors/schedulesSelectors';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from 'ui/shared/messages';
import { CheckField } from 'ui/shared/controls';
import { mapValues, omit, groupBy } from 'lodash';
import { useDeviceTypes } from 'hooks/useDeviceTypes/useDeviceTypes';
import { useSelector } from 'react-redux';
import { Loading } from 'ui/shared/loading';
import { getDevicesCapableOfTheme } from './getDevicesCapableOfTheme';

require('./ScheduleThemeForm.css');

export type Props = {
    themeId: ThemeId;
    buildingId: BuildingId;
    saving: boolean;
    errorMessage: string;
    onAccept(devices: Record<DeviceId, boolean>): void;
    onCancel(): void;
};


export const ScheduleThemeForm = (props: Props) => {
    const { t } = useTranslation();
    const { themeId, saving, errorMessage, onAccept, onCancel } = props;

    const devices = useSelector((state: State) => getBuildingDevices(state, props));
    const theme = useSelector((state: State) => getTheme(state, props));
    const themeName = theme?.displayName || props.themeId;

    const { deviceTypes, isLoadingDeviceTypes } = useDeviceTypes();

    const scheduleByDeviceId = useSelector(getSchedules);

    const [ values, setValues ] = useState<Record<DeviceId, boolean>>(() =>
        mapValues(devices || {}, d => scheduleByDeviceId[d.id]?.rules.some(r => r.themeId === themeId))
    );
    
    const capableDevices =
        isLoadingDeviceTypes || !devices || !theme
            ? []
            : getDevicesCapableOfTheme(devices, deviceTypes, theme);

    const isLoading = isLoadingDeviceTypes || !devices;


    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.currentTarget;
        setValues(v => ({
            ...v,
            [name]: checked,
        }));
    }, [ setValues ]);

    const handleSelectAll = useCallback(() => {
        setValues(() =>
            mapValues(groupBy(capableDevices, d => d.id), _ => true)
        );
    }, [ setValues ]);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Calculate which unselected devices were selected at the beginning and so
        // we need to pass them along so that the server can remove the schedule
        const devicesToExclude = Object.keys(values).filter(deviceId => {
            // Include device that was checked
            if (values[deviceId]) {
                return false;
            }

            // Exclude device that had no previous schedule
            const scheduleMap = scheduleByDeviceId[deviceId];
            if (!scheduleMap) {
                return true;
            }

            // Include device if it previously had a rule for the theme
            const { rules } = scheduleMap;
            return rules.every(r => r.themeId !== props.themeId);
        });

        const devicesToChange = omit(values, devicesToExclude);
        onAccept(devicesToChange);
    };

    return (
        <form
            id="schedule-theme-form"
            onSubmit={ handleSubmit }
            className={ `form${saving ? ' loading' : ''}` }
        >
            <div className="ui grid">
                <div className="row">
                    <div className="sixteen wide column">
                        <label style={ { marginRight: '1em' } }>
                            { t('SCHEDULE.THEME_DIALOG.LABELS.THEME') }
                        </label>
                        <span>
                            { themeName }
                        </span>
                    </div>
                </div>
                { isLoading &&
                    <Loading/>
                }
                { capableDevices.map(d =>
                    <div
                        key={ d.id }
                        className="device-row"
                    >
                        <CheckField
                            id={ `schedule-device-${d.id}` }
                            label={ d.displayName || d.id }
                            input={ {
                                name: d.id,
                                checked: !!values[d.id],
                                onChange: handleChange,
                            } }
                        />
                        <span className="device-type-name">
                            { deviceTypes && deviceTypes[d.deviceTypeId].displayName }
                        </span>
                    </div>
                ) }
                { !isLoading && !capableDevices.length &&
                    <div style={ { opacity: .5 } }>
                        { t('SCHEDULE.THEME_DIALOG.NO_COMPATIBLE_DEVICES') }
                    </div>
                }
            </div>

            <div className="button-row">
                <button
                    className="ui primary submit button"
                    type="submit"
                    disabled={ saving || !capableDevices.length }
                >
                    { t('COMMAND.SCHEDULE') }
                </button>
                
                { capableDevices.length > 0 &&
                    <button
                        className="ui button"
                        type="button"
                        disabled={ saving }
                        onClick={ handleSelectAll }
                    >
                        { t('COMMAND.SELECT_ALL') }
                    </button>
                }

                <button
                    className="ui button"
                    type="button"
                    disabled={ saving }
                    onClick={ onCancel }
                >
                    { t('COMMAND.CANCEL') }
                </button>
            </div>

            <ErrorMessage
                message={ errorMessage }
                fullWidth={ true }
            />
        </form>
    );
};
